/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Toolbar, Button, Typography, Box, Tabs, Tab, CircularProgress } from '@mui/material';
import ZampSettings from './zamp_settings';
import DateRangePicker from './historical_sync';
import Logger from './Logs';
import DisconnectPage from "./disconnect_page";
import Integration from "./Integration";
import ConnectPage from "./ConnectPage";
import ErrorPage from "./ErrorPage";
import SessionExpiredDialog from "./components/SessionExpiredDialog";
import TaxCodeMappingUtility from "./taxcode_mapping";
import { navLogoUrl } from "./brandAssets";

import CustomerEntityMappingUtility from "./customerentity_mapping";

const brandCdn = (process.env.REACT_APP_BRAND_CDN_URL || "https://d5vx28obntjzr.cloudfront.net").replace(/\/$/, "");
// const navLogoUrl = `${brandCdn}/logos/SVG/Wordmark/White-Wordmark.svg`;

function App() {
  const location = useLocation();
  const hideTabsOnPaths = ['/disconnect', '/launch', '/error'];
  const shouldHideTabs = hideTabsOnPaths.some(path => location.pathname.startsWith(path));

  return (
    <>
      <NavBar />
      <SessionExpiredDialog />
      <Box mt={8}>
        {!shouldHideTabs && <TabsSection />} {/* Conditionally hide TabsSection */}
        <Routes>
          <Route path="/settings/:realmId?" element={<ZampSettings />} />
          <Route path="/services/:realmId?" element={<DateRangePicker />} />
          <Route path="/logs/:realmId?" element={<Logger />} />
          <Route path="/" element={<Navigate to={`/launch`} replace />} />
          <Route path="/integration/:realmId?" element={<Integration />} />
          <Route path="/disconnect" element={<DisconnectPage />} />
          <Route path="/launch" element={<ConnectPage />} />
          <Route path="/taxcodemapping/:tenantId?" element={<TaxCodeMappingUtility />} />
          <Route path="/customerentitymapping/:tenantId?" element={<CustomerEntityMappingUtility />} />
          <Route path="/error" element={<ErrorPage />} />
        </Routes>
      </Box>
    </>
  );
}

function NavBar() {
  const navigate = useNavigate();
  const [realmId, setRealmId] = React.useState(sessionStorage.getItem("realmId"));

  useEffect(() => {
    const handleRealmChange = () => {
      setRealmId(sessionStorage.getItem("realmId"));
    };

    window.addEventListener("realmIdChange", handleRealmChange);
    return () => window.removeEventListener("realmIdChange", handleRealmChange);
  }, []);
  const contactUsUrl = process.env.REACT_APP_CONTACT_US_URL;
  const handleContactUsClick = () => {
    window.open(contactUsUrl, "_blank"); // Opens in a new tab
  };

  const handleLogoutClick = () => {
    sessionStorage.clear();
    // Notify tab/nav state (realmId, connected status, etc.) that the session was cleared
    window.dispatchEvent(new Event("realmIdChange"));
    // Stay in-app: land on the Integration tab instead of redirecting out to QBO's sign-in page
    navigate("/integration");
  };

  return (
    <AppBar position="fixed" color="primary">
      <Toolbar>
        <Typography variant="h6" sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
                  <img src={navLogoUrl} alt="Zamp" style={{ height: 22, marginRight: 8, marginTop: 5 }} />
        </Typography>
        <Button color="inherit" onClick={handleContactUsClick}>
          Contact Us
        </Button>
        <Button color="inherit" onClick={handleLogoutClick} disabled={!realmId}>
          Logout
        </Button>
      </Toolbar>
    </AppBar>
  );
}

function TabsSection() {
  const location = useLocation();
  const navigate = useNavigate();
  const [realmId, setRealmId] = React.useState(sessionStorage.getItem("realmId"));
  const [value, setValue] = React.useState(0);

  useEffect(() => {
    // Set correct tab based on pathname
    if (location.pathname.startsWith("/integration")) setValue(0);
    else if (location.pathname.startsWith("/settings")) setValue(1);
    else if (location.pathname.startsWith("/services")) setValue(2);
    else if (location.pathname.startsWith("/taxcodemapping")) setValue(3);
    else if (location.pathname.startsWith("/customerentitymapping")) setValue(4);
    else if (location.pathname.startsWith("/logs")) setValue(5);
  }, [location.pathname]);

  useEffect(() => {
    const handleRealmChange = () => {
      setRealmId(sessionStorage.getItem("realmId"));
    };

    window.addEventListener("realmIdChange", handleRealmChange);
    return () => window.removeEventListener("realmIdChange", handleRealmChange);
  },);

  const handleChange = (event, newValue) => {
    setValue(newValue);

    switch (newValue) {
      case 0:
        navigate(`/integration/${realmId || ""}`);
        break;
      case 1:
        navigate(`/settings/${realmId}`);
        break;
      case 2:
        navigate(`/services/${realmId}`);
        break;
      case 3: navigate(`/taxcodemapping/${realmId}`);
      break;
      case 4:
        navigate(`/customerentitymapping/${realmId}`);
        break;
      case 5:
        navigate(`/logs/${realmId}`);
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{ width: '100%', padding: 2 }}>
      <Tabs
        value={value}
        onChange={handleChange}
        textColor="inherit"
        indicatorColor="secondary"
        centered
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Integration" />
        <Tab label="Zamp Tax Settings" disabled={!realmId} />             
        <Tab label="Historical Data Sync" disabled={!realmId} />
        <Tab label="Product Tax Code Mapping" disabled={!realmId} />
        <Tab label="Customer Entity Mapping" disabled={!realmId} />
        <Tab label="Logs" disabled={!realmId} />
      </Tabs>
    </Box>
  );
}

export default App;